import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { ConversionFeature } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import {
  CHECKOUT_EXPERIENCE_PAGE_TAB_QUERY_PARAM,
  CheckoutExperiencePageTab,
} from "@redotech/redo-web/routing-common/checkout-optimization/constants";
import { Navigate } from "react-router-dom";
import { ConversionFeatureGate } from "../../../conversion/locked/conversion-feature-gate";
import { spotlightRoutes } from "../../../spotlights/route";

const checkoutExperienceBreadcrumb = breadcrumbSlot();
const splitBreadcrumb = breadcrumbSlot();
const ppuBreadcrumb = breadcrumbSlot();
const flowViewBreadcrumb = breadcrumbSlot();

export const conversionRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Conversion", breadcrumbNavigate: false },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNavbar}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      {
        path: "welcome",
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { WelcomePage } =
            await import("../../../conversion/welcome/welcome-page");
          return { Component: WelcomePage };
        },
      },
      { path: "onboard", element: <Navigate replace to="../dashboard" /> },
      {
        path: "dashboard",
        handle: { breadcrumb: "Dashboard" },
        async lazy() {
          const { DashboardPage } =
            await import("../../../conversion/dashboard/dashboard-page");
          return { Component: DashboardPage };
        },
      },
      {
        path: "kyc",
        handle: { breadcrumb: "Identity verification" },
        async lazy() {
          const { GaleKycRedirectPage } =
            await import("../../../conversion/gale-kyc/gale-kyc-redirect-page");
          return { Component: GaleKycRedirectPage };
        },
      },
      {
        path: "intent-setup",
        handle: { breadcrumb: "Intent rescue setup" },
        async lazy() {
          const { IntentSetupPage } =
            await import("../../../spotlights/setup-wizard/intent-setup-page");
          return { Component: IntentSetupPage };
        },
      },
      {
        path: "ppu-intent-setup",
        handle: { breadcrumb: "Conversion setup" },
        async lazy() {
          const { PpuIntentSetupPage } =
            await import("../../../conversion/self-serve/ppu-intent-setup-page");
          return { Component: PpuIntentSetupPage };
        },
      },
      {
        path: "pricing-tests",
        handle: { breadcrumb: "Pricing tests" },
        element: (
          <ConversionFeatureGate feature={ConversionFeature.PRICING_TESTS} />
        ),
        children: [
          {
            index: true,
            async lazy() {
              const { PricingTestsPage } =
                await import("../../../conversion/pricing-tests/pricing-tests-page");
              return { Component: PricingTestsPage };
            },
          },
        ],
      },
      {
        path: "ab-tests",
        handle: { breadcrumb: "A/B tests" },
        element: <ConversionFeatureGate feature={ConversionFeature.AB_TESTS} />,
        children: [
          {
            path: "edit-tree",
            async lazy() {
              const { EditCheckoutTreePage } =
                await import("../../../conversion/checkout-tree/edit-checkout-tree-page");
              return { Component: EditCheckoutTreePage };
            },
          },
          {
            path: "create",
            async lazy() {
              const { CreateCheckoutAbTestPage } =
                await import("../../../conversion/checkout-tree/create-checkout-ab-test-page");
              return { Component: CreateCheckoutAbTestPage };
            },
          },
          {
            path: "",
            index: true,
            async lazy() {
              const { AbTestsPage } =
                await import("../../../conversion/ab-tests/ab-tests-page");
              return { Component: AbTestsPage };
            },
          },
          {
            path: "ppu/:testId",
            handle: { breadcrumb: ppuBreadcrumb },
            async lazy() {
              const { PpuAbTestDetailPage } =
                await import("../../../conversion/ab-tests/ppu-detail-page/ppu-ab-test-detail-page");
              return {
                element: <PpuAbTestDetailPage breadcrumb={ppuBreadcrumb} />,
              };
            },
          },
          {
            path: ":splitId",
            handle: { breadcrumb: splitBreadcrumb },
            async lazy() {
              const { AbTestDetailPage } =
                await import("../../../conversion/ab-tests/detail-page/ab-test-detail-page");
              return {
                element: <AbTestDetailPage breadcrumb={splitBreadcrumb} />,
              };
            },
          },
        ],
      },
      {
        path: "checkouts",
        handle: { breadcrumb: "Checkouts" },
        element: (
          <ConversionFeatureGate feature={ConversionFeature.CHECKOUTS} />
        ),
        children: [
          {
            path: "edit-tree",
            async lazy() {
              const { EditCheckoutTreePage } =
                await import("../../../conversion/checkout-tree/edit-checkout-tree-page");
              return { Component: EditCheckoutTreePage };
            },
          },
          {
            path: "flows",
            handle: { breadcrumb: "Flows" },
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    replace
                    to={`..?${CHECKOUT_EXPERIENCE_PAGE_TAB_QUERY_PARAM}=${CheckoutExperiencePageTab.ALL_FLOWS}`}
                  />
                ),
              },
              {
                path: ":recordId/events/:eventId",
                handle: { breadcrumb: flowViewBreadcrumb },
                async lazy() {
                  const { CheckoutTreeFlowViewPage } =
                    await import("../../../conversion/checkout-experiences/checkout-tree-flow-view-page");
                  return {
                    element: (
                      <CheckoutTreeFlowViewPage
                        breadcrumb={flowViewBreadcrumb}
                      />
                    ),
                  };
                },
              },
              {
                path: ":recordId",
                handle: { breadcrumb: flowViewBreadcrumb },
                async lazy() {
                  const { CheckoutTreeFlowViewPage } =
                    await import("../../../conversion/checkout-experiences/checkout-tree-flow-view-page");
                  return {
                    element: (
                      <CheckoutTreeFlowViewPage
                        breadcrumb={flowViewBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
          {
            path: ":checkoutExperienceId/parcels",
            async lazy() {
              const { UpdateCheckoutExperienceParcelsPage } =
                await import("../../../conversion/checkout-experiences/parcels/update-checkout-experience-parcels-page");
              return { Component: UpdateCheckoutExperienceParcelsPage };
            },
          },
          {
            path: ":checkoutExperienceId/upsell-page/:upsellId",
            async lazy() {
              const { UpdateCheckoutExperienceOneClickUpsellPage } =
                await import("../../../conversion/checkout-experiences/one-click-upsell/update-checkout-experience-one-click-upsell-page");
              return { Component: UpdateCheckoutExperienceOneClickUpsellPage };
            },
          },
          {
            handle: { breadcrumb: checkoutExperienceBreadcrumb },
            path: ":checkoutExperienceId",
            lazy: async () => {
              const { CheckoutExperienceDetailPage } =
                await import("../../../conversion/checkout-experiences/checkout-page/checkout-experience-detail-page");
              return {
                element: (
                  <CheckoutExperienceDetailPage
                    breadcrumb={checkoutExperienceBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: "",
            index: true,
            async lazy() {
              const { CheckoutExperiencePage } =
                await import("../../../conversion/checkout-experiences/checkout-experience-page");
              return { Component: CheckoutExperiencePage };
            },
          },
        ],
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics", breadcrumbNavigate: false },
        children: [
          {
            path: "checkout",
            handle: { breadcrumb: "Checkout" },
            element: (
              <ConversionFeatureGate
                feature={ConversionFeature.ANALYTICS_CHECKOUT}
              />
            ),
            children: [
              {
                index: true,
                async lazy() {
                  const { CheckoutAnalyticsPage } =
                    await import("../../../conversion/analytics/checkout-analytics-page");
                  return { Component: CheckoutAnalyticsPage };
                },
              },
            ],
          },
          {
            path: "post-purchase",
            handle: { breadcrumb: "Post-purchase" },
            element: (
              <ConversionFeatureGate
                feature={ConversionFeature.ANALYTICS_POST_PURCHASE}
              />
            ),
            children: [
              {
                index: true,
                async lazy() {
                  const { PostPurchaseAnalyticsPage } =
                    await import("../../../conversion/analytics/post-purchase-analytics-page");
                  return { Component: PostPurchaseAnalyticsPage };
                },
              },
            ],
          },
          {
            path: "package-protection",
            handle: { breadcrumb: "Package Protection" },
            element: <ConversionFeatureGate />,
            children: [
              {
                index: true,
                async lazy() {
                  const { PackageProtectionDashboardPage } =
                    await import("../../../conversion/analytics/package-protection/package-protection-dashboard-page");
                  return { Component: PackageProtectionDashboardPage };
                },
              },
            ],
          },
          {
            path: "reports",
            handle: {
              breadcrumb: "Custom reports",
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            element: (
              <ConversionFeatureGate
                feature={ConversionFeature.ANALYTICS_CUSTOM_REPORTS}
              />
            ),
            children: [
              {
                index: true,
                handle: { permission: OrderManagementPermissions.VIEW_REPORTS },
                async lazy() {
                  const { ConversionCustomReportsTab } =
                    await import("../../../analytics/reports/custom/conversion-custom-reports");
                  return { Component: ConversionCustomReportsTab };
                },
              },
              {
                path: "custom/:customReportId",
                handle: {
                  breadcrumb: "Custom",
                  permission: OrderManagementPermissions.VIEW_REPORTS,
                },
                async lazy() {
                  const { ConversionCustomReportPage } =
                    await import("../../../analytics/reports/custom/conversion-custom-reports");
                  return { Component: ConversionCustomReportPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "post-purchase",
        handle: { breadcrumb: "Post-purchase" },
        element: (
          <ConversionFeatureGate feature={ConversionFeature.POST_PURCHASE} />
        ),
        children: [
          {
            path: "edit-flow",
            async lazy() {
              const { EditPostPurchaseFlowPage } =
                await import("../../../conversion/post-purchase/edit-post-purchase-flow-page");
              return { Component: EditPostPurchaseFlowPage };
            },
          },
          {
            path: "create-ab-test",
            async lazy() {
              const { CreatePostPurchaseAbTestPage } =
                await import("../../../conversion/post-purchase/create-post-purchase-ab-test-page");
              return { Component: CreatePostPurchaseAbTestPage };
            },
          },
          {
            path: "create",
            async lazy() {
              const { PostPurchaseBuilder } =
                await import("../../../conversion/post-purchase/builder/post-purchase-builder");
              return { Component: PostPurchaseBuilder };
            },
          },
          {
            path: ":upsellId",
            async lazy() {
              const { PostPurchaseBuilder } =
                await import("../../../conversion/post-purchase/builder/post-purchase-builder");
              return { Component: PostPurchaseBuilder };
            },
          },
          {
            path: "",
            index: true,
            async lazy() {
              const { PostPurchaseHomepage } =
                await import("../../../conversion/post-purchase/post-purchase-homepage");
              return { Component: PostPurchaseHomepage };
            },
          },
        ],
      },
      {
        path: "discount-rules",
        handle: { breadcrumb: "Discount rules" },
        element: (
          <ConversionFeatureGate feature={ConversionFeature.DISCOUNT_RULES} />
        ),
        children: [
          {
            path: "create",
            async lazy() {
              const { DiscountRuleEditorPage } =
                await import("../../../conversion/discount-rules/discount-rule-editor-page");
              return { Component: DiscountRuleEditorPage };
            },
          },
          {
            path: ":ruleId",
            async lazy() {
              const { DiscountRuleEditorPage } =
                await import("../../../conversion/discount-rules/discount-rule-editor-page");
              return { Component: DiscountRuleEditorPage };
            },
          },
          {
            path: "",
            index: true,
            async lazy() {
              const { DiscountRulesListPage } =
                await import("../../../conversion/discount-rules/discount-rules-list-page");
              return { Component: DiscountRulesListPage };
            },
          },
        ],
      },
      {
        path: "spotlights",
        element: (
          <ConversionFeatureGate feature={ConversionFeature.SPOTLIGHTS} />
        ),
        children: spotlightRoutes,
      },
      {
        path: "widgets",
        handle: { breadcrumb: "Widgets" },
        element: <ConversionFeatureGate feature={ConversionFeature.WIDGETS} />,
        children: [
          {
            path: "create",
            async lazy() {
              const { WidgetEditorPage } =
                await import("../../../conversion/widgets/widget-editor-page");
              return { Component: WidgetEditorPage };
            },
          },
          {
            path: ":widgetId",
            async lazy() {
              const { WidgetEditorPage } =
                await import("../../../conversion/widgets/widget-editor-page");
              return { Component: WidgetEditorPage };
            },
          },
          {
            path: "",
            index: true,
            async lazy() {
              const { WidgetsListPage } =
                await import("../../../conversion/widgets/widgets-list-page");
              return { Component: WidgetsListPage };
            },
          },
        ],
      },
    ],
  },
];

function isCreateAbTestPage(url: string): boolean {
  return /conversion\/ab-tests\/create/.test(url);
}

function isUpdateCheckoutExperienceParcelsPage(url: string): boolean {
  return /conversion\/checkouts\/[^/]+\/parcels/.test(url);
}

function isUpsellPageBuilderPage(url: string): boolean {
  return (
    /conversion\/checkouts\/[^/]+\/upsell-page\/[^/]+/.test(url) ||
    /conversion\/post-purchase\/[^/]+/.test(url)
  );
}

function isEditCheckoutTreePage(url: string): boolean {
  return (
    /conversion\/checkouts\/edit-tree/.test(url) ||
    /conversion\/ab-tests\/edit-tree/.test(url)
  );
}

function isEditPostPurchaseFlowPage(url: string): boolean {
  return /conversion\/post-purchase\/edit-flow/.test(url);
}

function isCreatePostPurchaseAbTestPage(url: string): boolean {
  return /conversion\/post-purchase\/create-ab-test/.test(url);
}

function isWidgetEditorPage(url: string): boolean {
  return /conversion\/widgets\/[^/]+/.test(url);
}

function isSpotlightBuilderPage(url: string): boolean {
  return (
    /conversion\/spotlights\/builder\//.test(url) ||
    /conversion\/spotlights\/new/.test(url)
  );
}

function isSpotlightListPage(url: string): boolean {
  return /conversion\/spotlights\/?$/.test(url) && !isSpotlightBuilderPage(url);
}

function hidePadding(url: string): boolean {
  return !isSpotlightListPage(url);
}

function hideNavbar(url: string): boolean {
  return (
    isCreateAbTestPage(url) ||
    isUpdateCheckoutExperienceParcelsPage(url) ||
    isUpsellPageBuilderPage(url) ||
    isEditCheckoutTreePage(url) ||
    isEditPostPurchaseFlowPage(url) ||
    isCreatePostPurchaseAbTestPage(url) ||
    isWidgetEditorPage(url) ||
    isSpotlightBuilderPage(url)
  );
}

function hideHeader(url: string): boolean {
  return (
    isCreateAbTestPage(url) ||
    isUpdateCheckoutExperienceParcelsPage(url) ||
    isUpsellPageBuilderPage(url) ||
    isEditCheckoutTreePage(url) ||
    isEditPostPurchaseFlowPage(url) ||
    isCreatePostPurchaseAbTestPage(url) ||
    isWidgetEditorPage(url) ||
    isSpotlightBuilderPage(url)
  );
}
